import type { ReactNode } from "react";
import type { Block, Token } from "@/lib/tool-kit/chemistry/dimensional";
import {
  formatExact,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

/**
 * The factor-label chain drawn as stacked fractions, with cancelled units struck through.
 * Cancelled units also carry hidden text for screen readers, since a line-through alone is
 * only visual.
 */

function UnitToken({
  token,
  cancelledLabel,
}: {
  token: Token;
  cancelledLabel: string;
}) {
  const kept = token.exp - token.cancelled;
  const cancel = (exp: number): ReactNode => (
    <s className="text-muted-foreground decoration-2 decoration-primary">
      {token.unit}
      {exp > 1 ? <sup>{exp}</sup> : null}
      <span className="sr-only"> ({cancelledLabel})</span>
    </s>
  );
  const keep = (exp: number): ReactNode => (
    <span>
      {token.unit}
      {exp > 1 ? <sup>{exp}</sup> : null}
    </span>
  );
  if (token.cancelled === 0) {
    return keep(token.exp);
  }
  if (kept === 0) {
    return cancel(token.exp);
  }
  return (
    <>
      {cancel(token.cancelled)}
      {keep(kept)}
    </>
  );
}

function Half({
  value,
  tokens,
  fmt,
  cancelledLabel,
}: {
  value: number | null;
  tokens: readonly Token[];
  fmt: NumberFormatter;
  cancelledLabel: string;
}) {
  return (
    <span className="flex items-baseline gap-1 whitespace-nowrap px-1">
      {value === null ? null : <span>{formatExact(fmt, value)}</span>}
      {tokens.map((token) => (
        <UnitToken
          cancelledLabel={cancelledLabel}
          key={token.unit}
          token={token}
        />
      ))}
    </span>
  );
}

function BlockView({
  block,
  fmt,
  cancelledLabel,
}: {
  block: Block;
  fmt: NumberFormatter;
  cancelledLabel: string;
}) {
  if (block.kind === "start" && block.denominator.tokens.length === 0) {
    return (
      <Half
        cancelledLabel={cancelledLabel}
        fmt={fmt}
        tokens={block.numerator.tokens}
        value={block.numerator.value}
      />
    );
  }
  return (
    <span className="inline-flex flex-col items-center">
      <Half
        cancelledLabel={cancelledLabel}
        fmt={fmt}
        tokens={block.numerator.tokens}
        value={block.numerator.value}
      />
      <span aria-hidden="true" className="h-px w-full bg-foreground/60" />
      <Half
        cancelledLabel={cancelledLabel}
        fmt={fmt}
        tokens={block.denominator.tokens}
        value={block.denominator.value}
      />
    </span>
  );
}

export function FractionChain({
  blocks,
  result,
  fmt,
  cancelledLabel,
}: {
  blocks: readonly Block[];
  /** The rounded answer with its units, drawn after the equals sign. */
  result: string;
  fmt: NumberFormatter;
  cancelledLabel: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card p-4">
      <div className="flex min-w-max items-center gap-2 font-mono text-foreground text-sm sm:text-base">
        {blocks.map((block, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the chain is positional and never reordered
          <span className="flex items-center gap-2" key={index}>
            {index === 0 ? null : <span aria-hidden="true">×</span>}
            <BlockView
              block={block}
              cancelledLabel={cancelledLabel}
              fmt={fmt}
            />
          </span>
        ))}
        <span aria-hidden="true">=</span>
        <strong>{result}</strong>
      </div>
    </div>
  );
}
